import { Controller, RegisterOptions, useFormContext, useFormState } from 'react-hook-form';
import { getErrorByPath } from '../formComponents.utils';
import BaseTextField from './BaseTextField';
import { INPUT_MODES, InputMode } from './textField.constants';

interface ControlledTextFieldProps {
  name: string;
  rules?: RegisterOptions;
  validateAs?: 'number' | 'text';
  inputMode?: InputMode;
  onBlurNormalize?: (value: string) => string;
  showCharCount?: boolean;
  showErrorInline?: boolean;
  reserveErrorSpace?: boolean;
  [key: string]: any;
}

export default function ControlledTextField(props: ControlledTextFieldProps) {
  const {
    name,
    rules = {},
    validateAs,
    inputMode,
    onBlurNormalize,
    showCharCount = false,
    showErrorInline = true,
    reserveErrorSpace = false,
    ...rest
  } = props;

  const { control, clearErrors } = useFormContext();
  const { errors } = useFormState({ name });

  const error = getErrorByPath(errors, name);
  const errorMessage = showErrorInline ? (error?.message as string | undefined) : undefined;
  const hasError = showErrorInline ? Boolean(error) : false;

  const resolvedInputMode: InputMode =
    inputMode ?? (validateAs === 'number' ? INPUT_MODES.NUMERIC : INPUT_MODES.TEXT);

  const maxLength = rules?.maxLength as { value: number } | undefined;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const value: string = field.value ?? '';
        const helperText =
          showCharCount && maxLength ? `${String(value).length} / ${maxLength.value}` : undefined;

        const handleBlur = () => {
          field.onBlur();
          if (!onBlurNormalize) return;
          const normalized = onBlurNormalize(field.value);
          if (normalized !== field.value) field.onChange(normalized);
          clearErrors(name);
        };

        return (
          <BaseTextField
            {...rest}
            id={rest.id || name}
            value={value}
            inputMode={resolvedInputMode}
            error={hasError}
            errorMessage={errorMessage}
            showErrorInline={showErrorInline}
            reserveErrorSpace={reserveErrorSpace}
            helperText={helperText}
            onChange={(e: any) => {
              field.onChange(e);
              clearErrors(name);
            }}
            onBlur={handleBlur}
            inputProps={{
              minLength: (rules?.minLength as any)?.value,
              maxLength: maxLength?.value,
            }}
          />
        );
      }}
    />
  );
}
